import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Shield, Activity, TrendingUp, Wifi, WifiOff, Store, Zap } from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import RecentAlerts from "@/components/RecentAlerts";
import { useWebSocket } from "@/hooks/useWebSocket";
import { useDashboardData } from "@/hooks/useDashboardData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];

// Chart configuration constants
const CHART_MARGIN = { top: 10, right: 10, left: -10, bottom: 0 };
const AXIS_TICK_STYLE = { fontSize: 10, fill: '#71717A' };
const AXIS_LINE_STYLE = { stroke: '#e4e4e7' };
const TOOLTIP_STYLE = { 
  backgroundColor: 'white', 
  border: '1px solid #e4e4e7', 
  borderRadius: '8px',
  fontSize: '12px',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
};
const LEGEND_STYLE = { fontSize: '10px', paddingTop: '20px' };

export default function Dashboard() {
  const sessionStartedRef = useRef(false);
  const [externalSourceActive, setExternalSourceActive] = useState(false);
  
  const {
    stats,
    blockedByReason,
    loading,
    storeTransactions,
    fetchDashboardStats,
    fetchStoreData,
    updateStatsFromTransaction
  } = useDashboardData();

  // Check if external transaction source is active
  const checkExternalSource = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/session/status`);
      const { mode, external_data_active } = response.data;
      setExternalSourceActive(external_data_active || mode === 'external');
    } catch (error) {
      setExternalSourceActive(false);
    }
  }, []);

  // Check external source status periodically
  useEffect(() => {
    checkExternalSource();
    const interval = setInterval(checkExternalSource, 5000);
    return () => clearInterval(interval);
  }, [checkExternalSource]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === 'transaction') {
      updateStatsFromTransaction(message);
      
      // Show toast notifications for suspicious transactions
      const score = (message.data.fraud_score * 100).toFixed(0);
      if (message.data.status === 'blocked') {
        toast.error(
          <div className="flex flex-col gap-1">
            <div className="font-bold flex items-center gap-2">
              <span className="text-red-500">🚨 BLOCKED</span>
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{score}% risk</span>
            </div>
            <div className="text-sm">${message.data.amount.toFixed(2)} at {message.data.merchant}</div>
            {message.data.block_reason && (
              <div className="text-xs text-zinc-500">{message.data.block_reason}</div>
            )}
          </div>,
          { duration: 5000 }
        );
      } else if (message.data.status === 'flagged') {
        toast.warning(
          <div className="flex flex-col gap-1">
            <div className="font-bold flex items-center gap-2">
              <span className="text-amber-600">⚠️ FLAGGED</span>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{score}% risk</span>
            </div>
            <div className="text-sm">${message.data.amount.toFixed(2)} at {message.data.merchant}</div>
          </div>,
          { duration: 4000 }
        );
      }
    }
  }, [updateStatsFromTransaction]);

  const { wsConnected, connectWebSocket, disconnect, sendPing } = useWebSocket(handleWebSocketMessage);

  // Auto-start session for dev mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isDevMode = params.get('dev') === 'true';
    
    if (isDevMode && !sessionStartedRef.current) {
      sessionStartedRef.current = true;
      axios.post(`${API}/session/start`)
        .catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Could not start dev session:', error.message);
          }
        });
    }
  }, []);

  // Initialize data and WebSocket
  useEffect(() => {
    fetchDashboardStats(true);
    fetchStoreData();
    connectWebSocket();
    
    const interval = setInterval(() => fetchDashboardStats(), 5000);
    const storeInterval = setInterval(fetchStoreData, 5000);
    
    return () => {
      clearInterval(interval);
      clearInterval(storeInterval);
      disconnect();
    };
  }, [fetchDashboardStats, fetchStoreData, connectWebSocket, disconnect]);

  // WebSocket ping
  useEffect(() => {
    const pingInterval = setInterval(sendPing, 25000);
    return () => clearInterval(pingInterval);
  }, [sendPing]);

  if (loading) {
    return (
      <div className="py-4" data-testid="dashboard-loading">
        <div className="text-center py-12 text-sm text-zinc-500">Loading dashboard...</div>
      </div>
    );
  }

  const fraudRate = stats ? ((stats.fraud_detected / stats.total_transactions) * 100).toFixed(2) : '0.00';

  return (
    <div className="py-4 pb-24" data-testid="dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-black tracking-tighter text-zinc-950">Dashboard</h2>
          <p className="text-xs text-zinc-600 mt-0.5">Real-time monitoring</p>
        </div>
        <div className="flex items-center gap-2">
          {externalSourceActive ? (
            // External source is sending transactions - show External Data badge
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
              <Wifi className="w-3 h-3 mr-1" /> External Data
            </Badge>
          ) : (
            // Demo mode - show Demo Data badge
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <Zap className="w-3 h-3 mr-1" /> Demo Data
            </Badge>
          )}
          {/* WebSocket connection indicator (always visible) */}
          {wsConnected ? (
            <Badge variant="outline" className="bg-zinc-50 text-zinc-600 border-zinc-200 text-[10px]">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5 animate-pulse"></span> Live
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-zinc-50 text-zinc-400 border-zinc-200 text-[10px]">
              <WifiOff className="w-3 h-3 mr-1" /> Offline
            </Badge>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard
          label="TOTAL"
          value={stats?.total_transactions?.toLocaleString() || '0'}
          icon={<Activity className="w-5 h-5 text-zinc-400" />}
          testId="total-transactions-card"
        />
        <StatCard
          label="FRAUD"
          value={stats?.fraud_detected?.toLocaleString() || '0'}
          icon={<AlertTriangle className="w-5 h-5 text-red-400" />}
          valueClass="text-red-600"
          testId="fraud-detected-card"
        />
        <StatCard
          label="RATE"
          value={`${fraudRate}%`}
          icon={<TrendingUp className="w-5 h-5 text-zinc-400" />}
          testId="fraud-rate-card"
        />
        <StatCard
          label="BLOCKED"
          value={stats?.total_blocked?.toLocaleString() || '0'}
          icon={<Shield className="w-5 h-5 text-emerald-400" />}
          testId="blocked-card"
        />
      </div>

      {/* Store Transactions Chart */}
      <Card className="p-4 bg-white border border-zinc-200 rounded-xl mb-6" data-testid="store-chart">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Store className="w-5 h-5 text-zinc-700" />
            <h3 className="text-base font-bold tracking-tight text-zinc-950">Live Transactions by Store</h3>
            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
              ● Streaming
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-[10px]">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-zinc-800 rounded"></div>
              <span className="text-zinc-600">Total</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span className="text-zinc-600">Blocked</span>
            </div>
          </div>
        </div>
        
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={storeTransactions} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={AXIS_TICK_STYLE} axisLine={AXIS_LINE_STYLE} tickLine={false} />
              <YAxis tick={AXIS_TICK_STYLE} axisLine={AXIS_LINE_STYLE} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="transactions" fill="#0F172A" radius={[4, 4, 0, 0]} name="Total Transactions" />
              <Bar dataKey="blocked" fill="#DC2626" radius={[4, 4, 0, 0]} name="Blocked" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Blocked by Reason Chart */}
      <Card className="p-4 bg-white border border-zinc-200 rounded-xl mb-6" data-testid="blocked-reasons-chart">
        <h3 className="text-base font-bold tracking-tight text-zinc-950 mb-4">Blocked by Reason</h3>
        {blockedByReason.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-xs text-zinc-400">
            No blocked transactions yet
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={blockedByReason}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={3}
                dataKey="count"
                nameKey="reason"
                label={({ count }) => count}
                labelLine={false}
              >
                {blockedByReason.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend 
                wrapperStyle={LEGEND_STYLE}
                iconType="circle"
                iconSize={10}
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Recent Flagged Transactions */}
      <RecentAlerts />
    </div>
  );
}

// Extracted StatCard component
function StatCard({ label, value, icon, valueClass = '', testId }) {
  return (
    <Card className="p-4 bg-white border border-zinc-200 rounded-xl" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold text-zinc-500 tracking-wider mb-1">{label}</p>
          <p className={`text-2xl font-black tracking-tighter text-zinc-950 ${valueClass}`}>{value}</p>
        </div>
        {icon}
      </div>
    </Card>
  );
}
